import { CubePresenter, FlipIntent, Logger } from 'wdc-cube'

import { WaitingScope } from 'src/components/waiting/waiting.scopes'
import { MainPresenter } from 'src/main/Main.presenter'
import AnaliseKeys from '../analise.keys'
import analiseService from '../analise.service'
import type { AnaliseRow } from '../analise.types'
import { RegraFormKeys } from '../regra'
import regraService from '../regra/regra.service'
import { AnaliseFormKeys } from './analise_form.keys'
import AnaliseFormScope from './analise_form.scopes'

const LOG = Logger.get('AnaliseFormPresenter')

export class AnaliseFormPresenter extends CubePresenter<MainPresenter, AnaliseFormScope> {
    // :: Constructor
    public constructor(app: MainPresenter) {
        super(app, new AnaliseFormScope())
    }

    // :: Instance
    #analiseId?: number

    // :: Methods

    buscarAnaliseSelecionada = async () => {
        if (!this.#analiseId) return

        try {
            this.scope.waiting = new WaitingScope()
            const analise = await analiseService.fetchAnaliseInfo(this.#analiseId)
            if (analise) {
                this.scope.idSelected = analise.id!
                this.scope.tipo = analise.tipo
                this.scope.nome = analise.nome
                this.scope.status = analise.status
                this.scope.riscoBorderoDe = analise.riscoBorderoDe
                this.scope.riscoBorderoAte = analise.riscoBorderoAte
                this.scope.riscoCarteiraDe = analise.riscoCarteiraDe
                this.scope.riscoCarteiraAte = analise.riscoCarteiraAte
                this.scope.pararAoBloquear = analise.pararAoBloquear
                this.scope.obrigatorioNfe = analise.obrigatorioNfe
                this.scope.aplicarPf = analise.aplicarPf
                this.scope.modeloSerasa = analise.modeloSerasa
                this.scope.consultarSerasaDias = analise.consultarSerasaDias
                this.scope.naoRealizarNovaConsulta = analise.naoRealizarNovaConsulta
                this.scope.consultarSituacaoPfDias = analise.consultarSituacaoPfDias
                this.scope.naoRealizarNovaConsultaSituacaoPf = analise.naoRealizarNovaConsultaSituacaoPf
                this.scope.consultarProtestosDias = analise.consultarProtestosDias
                this.scope.naoRealizarNovaConsultaProtestos = analise.naoRealizarNovaConsultaProtestos

                this.scope.operacaoEmpresa = analise.operacaoEmpresa
                this.scope.regras = analise.regras ?? []
            }
        } catch (caught) {
            LOG.error('handleSave', caught)
            this.app.notify('error', (caught as Error).message)
            throw caught
        } finally {
            this.scope.waiting = undefined
        }
    }

    public override release() {
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new AnaliseFormKeys(this.app, intent)

        keys.parentSlot(this.scope)

        LOG.debug(`applyParameters ${keys.analiseId}`)

        if (initialization) {
            await this.#initialize()
        }

        if (this.#analiseId !== keys.analiseId) {
            this.#analiseId = keys.analiseId

            await this.buscarAnaliseSelecionada()
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new AnaliseFormKeys(this.app, intent)
        keys.analiseId = this.#analiseId
        LOG.debug(`publishParameters ${keys.analiseId}`)
    }

    async #initialize() {
        LOG.debug('initialize')

        this.scope.onClose = this.action(this.handleClose)
        this.scope.onSave = this.action(this.handleSave)
        this.scope.onAddRegra = this.action(this.handleAddRegra)
        this.scope.onEditRegra = this.action(this.handleEditRegra)
        this.scope.onDeleteRegra = this.action(this.handleDeleteRegra)
    }

    private async handleAddRegra() {
        const key = new RegraFormKeys(this.app)
        key.regraId = undefined
        key.analiseId = this.#analiseId!
        await key.flip()
    }

    private async handleEditRegra() {
        if (this.scope.idRegraSelecionada <= 0) {
            this.app.notify('warning', 'Regra deve ser selecionada')
            return
        }
        const key = new RegraFormKeys(this.app)
        key.regraId = this.scope.idRegraSelecionada
        key.analiseId = this.#analiseId!

        await key.flip()
    }

    private async handleDeleteRegra() {
        const idRemover = this.scope.idRegraSelecionada
        if (!confirm('Confirma a exclusão da regra?')) {
            return
        }
        if (this.#analiseId && idRemover > 0) {
            const deleted = await regraService.deleteRegra(this.#analiseId, idRemover)
            if (!deleted) return
        }
        this.scope.regras = this.scope.regras.filter((r) => r.id !== idRemover)
        this.app.notify('success', 'Removido com sucesso')
    }

    private async handleClose() {
        await new AnaliseKeys(this.app).flip()
    }

    private async handleSave(): Promise<AnaliseRow | undefined> {
        try {
            this.scope.saving = true

            const analiseSave = {
                tipo: this.scope.tipo,
                nome: this.scope.nome,
                status: this.scope.status as 'ATIVO' | 'INATIVO',
                riscoBorderoDe: this.scope.riscoBorderoDe,
                riscoBorderoAte: this.scope.riscoBorderoAte,
                riscoCarteiraDe: this.scope.riscoCarteiraDe,
                riscoCarteiraAte: this.scope.riscoCarteiraAte,
                pararAoBloquear: this.scope.pararAoBloquear,
                obrigatorioNfe: this.scope.obrigatorioNfe,
                aplicarPf: this.scope.aplicarPf,
                modeloSerasa: this.scope.modeloSerasa,
                consultarSerasaDias: this.scope.consultarSerasaDias,
                consultarSituacaoPfDias: this.scope.consultarSituacaoPfDias,
                naoRealizarNovaConsultaSituacaoPf: this.scope.naoRealizarNovaConsultaSituacaoPf,
                consultarProtestosDias: this.scope.consultarProtestosDias,
                naoRealizarNovaConsultaProtestos: this.scope.naoRealizarNovaConsultaProtestos,
                naoRealizarNovaConsulta: this.scope.naoRealizarNovaConsulta,
                operacaoEmpresa: this.scope.operacaoEmpresa
            }

            const retSalvar =
                this.scope.idSelected > 0
                    ? await analiseService.updateAnalise(this.scope.idSelected, analiseSave)
                    : await analiseService.saveAnalise(analiseSave)

            if (retSalvar) {
                this.app.notify('success', 'Salvo com sucesso')

                this.#analiseId = retSalvar.id!

                await this.buscarAnaliseSelecionada()
            }

            return retSalvar
        } catch (caught) {
            LOG.error('handleSave', caught)
            throw caught
        } finally {
            this.scope.saving = false
        }
    }
}

export default AnaliseFormPresenter
